'use client'

import { Anchor, Box, Flex } from '@mantine/core'

import { GTM_EVENTS } from '@/configs/dataTrackingEvent'
import { I18nLink } from '@/i18n/navigation'
import type { HomePageV2Header } from '@/services/cms/homePageV2'
import { trackGtmEvent } from '@/services/dataTracking'

import styles from './style.module.css'
import { buildTabHref, TRENDING_TAB_LINK } from './utils'

interface HeaderProps {
  data: HomePageV2Header
  locale: string
  /** link value of the active tab (already resolved with fallback) */
  activeLink: string
}

export default function Header({ data, locale, activeLink }: HeaderProps) {
  const tabs = data.tabs

  return (
    <Box bg="#080D1B" className={styles.headerBar}>
      <Box className={styles.headerBarInner}>
        <Flex h={49} gap={12} wrap="nowrap" className={styles.tabs}>
          {tabs.map(tab => {
            const isActive = tab.link === activeLink
            const tabStyle = {
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              whiteSpace: 'nowrap' as const,
              borderBottom: `2px solid ${isActive ? '#1199FA' : 'transparent'}`,
            }

            if (isActive) {
              return (
                <Box
                  key={tab.id ?? tab.link}
                  h="100%"
                  fw={600}
                  fz={{ base: 16, md: 18 }}
                  lh="20px"
                  c="#F7F9FA"
                  style={tabStyle}
                >
                  {tab.label}
                </Box>
              )
            }

            const commonProps = {
              h: '100%',
              fw: 600,
              fz: { base: 16, md: 18 },
              lh: '20px',
              underline: 'never' as const,
              c: '#A0A9BE',
              style: tabStyle,
            }

            const onNavTabClick = () => {
              trackGtmEvent({
                event: GTM_EVENTS.PriceNavTabClicked,
                tab_name: tab.link,
                page: activeLink,
              })
            }

            if (tab.link === TRENDING_TAB_LINK) {
              return (
                <Anchor
                  key={tab.id ?? tab.link}
                  href={buildTabHref(tab, locale)}
                  onClick={onNavTabClick}
                  {...commonProps}
                >
                  {tab.label}
                </Anchor>
              )
            }

            return (
              <Anchor
                key={tab.id ?? tab.link}
                component={I18nLink}
                href={`/price?asset_nav=${encodeURIComponent(tab.link)}`}
                locale={locale}
                onClick={onNavTabClick}
                {...commonProps}
              >
                {tab.label}
              </Anchor>
            )
          })}
        </Flex>
      </Box>
    </Box>
  )
}
